import { getValidToken } from '../keycloak.service';

// HIVE_API_URL is injected by Vite's `define`. When unset (the dev default —
// requests go through Vite's `/api` proxy on the same origin), fall back to
// the current origin so `new URL(path, base)` doesn't throw on an empty base.
const baseUrl =
    HIVE_API_URL ||
    (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');

async function request(getToken, method, path, { body, params, headers = {} } = {}) {
    const url = new URL(path, baseUrl);

    if (params) {
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) {
                url.searchParams.set(k, v);
            }
        });
    }

    const token = await getToken();

    const requestHeaders = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
    };

    const response = await fetch(url.toString(), {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
        throw await buildApiError(response);
    }

    if (response.status === 204) {return null;}

    const contentType = response.headers.get('content-type') || '';

    return contentType.includes('application/json')
        ? response.json()
        : response.text();
}

// Translate the backend's RFC 7807 + extensions response (see
// hive-common/web/GlobalExceptionHandler) into an Error subclass with the
// fields callers care about. Falls back gracefully when the body isn't JSON
// (e.g. an nginx 502 page) so error handlers never have to defend against a
// missing field.
async function buildApiError(response) {
    const contentType = response.headers.get('content-type') || '';
    let problem = null;

    if (contentType.includes('application/problem+json') || contentType.includes('application/json')) {
        try {
            problem = await response.json();
        } catch {
            problem = null;
        }
    }

    const error = new Error(
        (problem && (problem.detail || problem.title)) || `HTTP ${response.status}`
    );

    error.status = response.status;
    error.code = problem?.code || null;
    error.detail = problem?.detail || null;
    error.title = problem?.title || null;
    error.errors = Array.isArray(problem?.errors) ? problem.errors : [];
    error.traceId = problem?.traceId || null;
    error.problem = problem;
    error.response = response;

    return error;
}

/**
 * Builds an API client bound to a token provider. The default export uses the
 * CPO (`hive` realm) token; the platform client (see platform-api-client.js)
 * passes the `hive-platform` token provider so /api/v1/platform/* calls carry
 * the right realm's bearer token (ADR-007).
 */
export function createApiClient(getToken = getValidToken) {
    return {
        get: (path, opts) => request(getToken, 'GET', path, opts),
        post: (path, body, opts) => request(getToken, 'POST', path, { ...opts, body }),
        put: (path, body, opts) => request(getToken, 'PUT', path, { ...opts, body }),
        patch: (path, body, opts) => request(getToken, 'PATCH', path, { ...opts, body }),
        delete: (path, opts) => request(getToken, 'DELETE', path, opts),
    };
}

export default createApiClient(getValidToken);
